import React from 'react';
import PageTitle from '../../components/title/pageTitle/PageTitle';
import { Pagination, Table, Tabs, DatePicker, Select, Button, Tooltip } from 'antd';

import dayjs from 'dayjs';
import './Kpi.scss';
import { getEmployeeStatusColumns, getEmployeeListColumns } from './KpiHelper';
import { useKpiController } from './useKpiController';
import { Designation, Employee } from './types';
import AddKpiModal from './AddKpiModal';
import ViewKpiModal from './ViewKpiModal';
import EditKpiModal from './EditKpiModal';
import KpiDeleteConfirmationModal from './KpiDeleteConfirmationModal';
import { RootState, RolePermission } from '../../components/Sidebar/types';
import { useSelector } from 'react-redux';

const Kpi = () => {

  const {
    viewMode,
    handleTabChange,
    kpiData,
    loading,
    totalResults,
    currentPage,
    pageSize,
    onShowSizeChange,
    handlePageChange,
    setMonthYear,
    monthYear,
    designationList,
    selectedDesignationId,
    // setSelectedDesignationId,
    employeeList,
    handleDesignationChange,
    isAddKpiModalOpen,
    selectedEmployee,
    questionaireList,
    handleOpenAddKpiModal,
    handleCloseAddKpiModal,
    modalLoading,
    questionnaireInput,
    handleQuestionaireInputChange,
    isFormValid,
    handleSaveKpiModal,
    // handleSubmitBulkKpi,
    addedEmployeeIds,
    // bulkKpiPayload,
    isViewKpiModalOpen,
    viewKpiData,
    handleOpenViewKpiModal,
    handleCloseViewKpiModal,
    isEditKpiModalOpen,
    handleOpenEditKpiModal,
    handleCloseEditKpiModal,
    editKpiInput,
    handleEditKpiInputChange,
    handleUpdateKpi,
    isDeleteModalOpen,
    deleteMessage,
    modalLoadingDelete,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleDeleteKpi,
    selectedRowKeys,
    setSelectedRowKeys,
    submitLoading
  } = useKpiController();

  const rolePermissions = useSelector((state: RootState) => state?.rolePermissions?.rolePermissions);

  const kpiPermission = rolePermissions?.find(
    (item: RolePermission) => item?.component?.name?.trim() === "KPI"
  );


  const permissions = React.useMemo(() => ({
    isView: kpiPermission?.isView || false,
    isCreate: kpiPermission?.isCreate || false,
    isEdit: kpiPermission?.isEdit || false,
    isDelete: kpiPermission?.isDelete || false,
  }), [kpiPermission]);


  const handleAddKpi = (employee: Employee) => {
    handleOpenAddKpiModal(employee);
  };

  const isCurrentMonth = monthYear === dayjs().format('MM-YYYY');

  const columns = getEmployeeStatusColumns(handleOpenViewKpiModal, handleOpenEditKpiModal, (record) => handleOpenDeleteModal(record.employeeId), isCurrentMonth, permissions);
  const employeeColumns = getEmployeeListColumns(handleAddKpi, addedEmployeeIds, isCurrentMonth, permissions);

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };


  const kpiTableContent = (
    <>
      <Table
        rowSelection={permissions.isDelete && kpiData && kpiData.length > 0 ? rowSelection : undefined}
        columns={columns}
        dataSource={kpiData}
        loading={loading}
        pagination={false}
        scroll={{ y: 600, x: 'max-content' }}
        className="kpi-table"
        rowKey="employeeId"
      />
      {totalResults > 0 && (
        <div className='pagination mt-auto pt-5'>
          <Pagination
            className='float-right flex flex-row p-6'
            showSizeChanger
            onShowSizeChange={onShowSizeChange}
            current={currentPage}
            total={totalResults}
            pageSize={pageSize}
            onChange={handlePageChange}
          />
        </div>
      )}
    </>
  );

  const addKpiContent = (
    <>
      <Table
        columns={employeeColumns}
        dataSource={employeeList}
        loading={loading}
        pagination={false}
        scroll={{ y: 550, x: 'max-content' }}
        className="kpi-table"
        rowKey="employeeId"
      />
      {totalResults > 0 && (
        <div className='pagination mt-auto pt-5'>
          <Pagination
            className='float-right flex flex-row p-6'
            showSizeChanger
            onShowSizeChange={onShowSizeChange}
            current={currentPage}
            total={totalResults}
            pageSize={pageSize}
            onChange={handlePageChange}
          />
        </div>
      )}
    </>
  );



  return (
    <div className='page-container bg-customBg'>
      <div className="full-width-container">
        <PageTitle title="KPI Management" />
        <div className='bg-white tab-container p-4 rounded-lg flex flex-col kpi-page'>
          <Tabs
            activeKey={viewMode}
            onChange={handleTabChange}
            tabBarExtraContent={
              (viewMode === 'view' || viewMode === 'add') ? (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <Select
                    showSearch
                    placeholder="Select Designation"
                    style={{ width: 250 }}
                    className="kpi-designation-select"
                    suffixIcon={null}
                    value={selectedDesignationId}
                    onChange={(value: number) => handleDesignationChange(value)}
                    options={designationList?.map((d: Designation) => ({ label: d.name, value: d.designationId }))}
                    disabled={!designationList.length}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                  />
                  <DatePicker
                    picker="month"
                    format="MM-YYYY"
                    value={dayjs(monthYear, 'MM-YYYY')}
                    onChange={(date, dateString) => setMonthYear(dateString as string)}
                    allowClear={false}
                    className="kpi-date-picker"
                    style={{ width: 150 }}
                    disabledDate={(current) => {
                      const today = dayjs();
                      if (viewMode === 'view') {
                        return current && current > today.endOf('month');
                      }
                      const endOfMonth = today.endOf('month');
                      const daysRemaining = endOfMonth.diff(today, 'day');

                      const isCurrentMonth = current && current.isSame(today, 'month');
                      const isNextMonth = current && current.isSame(today.add(1, 'month'), 'month');

                      if (isCurrentMonth) return false;
                      if (isNextMonth && daysRemaining < 3) return false;

                      return true;
                    }}
                  // disabled={!designationList.length}
                  />
                  {viewMode === 'view' && permissions.isDelete && (
                    <Tooltip title="Delete">
                      <Button
                        type="primary"
                        shape="circle"
                        size="large"
                        className="ant-btn ant-btn-circle ant-btn-primary ant-btn-lg ant-btn-icon-only flex items-center justify-center px-3 md:mt-0 mt-3 md:mr-3"
                        onClick={() => handleOpenDeleteModal(selectedRowKeys as number[])}
                        disabled={selectedRowKeys.length === 0 || !isCurrentMonth}
                      >
                        <span className="ant-btn-icon material-symbols-outlined">
                          delete
                        </span>
                      </Button>
                    </Tooltip>
                  )}
                  {/* {viewMode === 'add' && (
                    <Button
                      type="primary"
                      className="addButton"
                      onClick={handleSubmitBulkKpi}
                      disabled={bulkKpiPayload.length === 0 || submitLoading}
                    >
                      Add
                    </Button>
                  )} */}
                </div>
              ) : null
            }
            items={[
              ...(permissions.isView ? [{
                key: 'view',
                label: 'KPI',
                children: kpiTableContent,
              }] : []),
              ...(permissions.isCreate ? [{
                key: 'add',
                label: 'Add KPI',
                children: addKpiContent,
              }] : [])
            ]}
          />
        </div>
      </div>

      <AddKpiModal
        isAddKpiModalOpen={isAddKpiModalOpen}
        handleCloseAddKpiModal={handleCloseAddKpiModal}
        selectedEmployee={selectedEmployee}
        handleSaveKpiModal={handleSaveKpiModal}
        isFormValid={isFormValid || false}
        questionaireList={questionaireList}
        modalLoading={modalLoading}
        questionnaireInput={questionnaireInput}
        handleQuestionaireInputChange={handleQuestionaireInputChange}
        submitLoading={submitLoading}
      />

      <ViewKpiModal
        isViewKpiModalOpen={isViewKpiModalOpen}
        handleCloseViewKpiModal={handleCloseViewKpiModal}
        selectedEmployee={selectedEmployee}
        viewKpiData={viewKpiData}
        modalLoading={modalLoading}
      />

      <EditKpiModal
        isEditKpiModalOpen={isEditKpiModalOpen}
        handleCloseEditKpiModal={handleCloseEditKpiModal}
        selectedEmployee={selectedEmployee}
        handleUpdateKpi={handleUpdateKpi}
        viewKpiData={viewKpiData}
        modalLoading={modalLoading}
        editKpiInput={editKpiInput}
        handleEditKpiInputChange={handleEditKpiInputChange}
      />

      <KpiDeleteConfirmationModal
        open={isDeleteModalOpen}
        handleCloseDelete={handleCloseDeleteModal}
        handleDelete={handleDeleteKpi}
        deleteMessage={deleteMessage}
        loading={modalLoadingDelete}
      />
    </div >
  );
};

export default Kpi;
